import React from 'react'
import styles from './TextInput.module.css'
import { AlertCircle } from 'lucide-react'

/**
 * TextInput
 *
 * Reusable labeled text field with optional icon, char counter,
 * helper text, and error state. Pure presentational component —
 * no API calls. Parent screens own the value/state.
 *
 * Usage:
 *   <TextInput
 *     label="Store name"
 *     value={form.storeName}
 *     onChange={(v) => updateField('storeName', v)}
 *     placeholder="e.g. Glam Studio by Priya"
 *   />
 *
 *   <TextInput
 *     label="Tagline"
 *     value={form.storeTagline}
 *     onChange={(v) => updateField('storeTagline', v)}
 *     maxLength={100}
 *     showCharCount
 *   />
 */
const TextInput = ({
    label,
    value,
    onChange,
    placeholder = '',
    type = 'text',
    icon: Icon,
    error,
    helperText,
    maxLength,
    showCharCount = false,
    disabled = false,
    required = false,
    autoFocus = false,
    onKeyDown,
    onBlur,
    name,
    id
}) => {
    const inputId = id || name || label?.replace(/\s+/g, '-').toLowerCase()

    return (
        <div className={styles.field}>
            {label && (
                <label className={styles.label} htmlFor={inputId}>
                    {Icon && <Icon size={12} className={styles.labelIcon} />}
                    {label}
                    {required && <span className={styles.required}>*</span>}
                </label>
            )}

            <input
                id={inputId}
                name={name}
                type={type}
                className={`${styles.input} ${error ? styles.inputError : ''}`}
                value={value ?? ''}
                onChange={(e) => onChange?.(e.target.value)}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
                placeholder={placeholder}
                maxLength={maxLength}
                disabled={disabled}
                autoFocus={autoFocus}
                aria-invalid={!!error}
                aria-describedby={error ? `${inputId}-error` : undefined}
            />

            <div className={styles.metaRow}>
                {error ? (
                    <span id={`${inputId}-error`} className={styles.errorText}>
                        <AlertCircle size={12} />
                        {error}
                    </span>
                ) : helperText ? (
                    <span className={styles.helperText}>{helperText}</span>
                ) : (
                    <span />
                )}

                {showCharCount && maxLength && (
                    <span className={styles.charCount}>
                        {(value || '').length}/{maxLength}
                    </span>
                )}
            </div>
        </div>
    )
}

export default TextInput